import AppContext from "PFApp/app_context";
import { useSession } from "PFApp/use_session";
import { Alert } from "PFComponents/alert";
import PageBox from "PFComponents/box/page_box";
import { Button } from "PFComponents/button";
import { usePusherEvent } from "PFCore/base/pusher";
import { useCustomValuesInvalidate } from "PFCore/hooks/queries/custom_fields/admin";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { ApiRoute } from "PFCore/utilities/routes";
import { GROWL_ACTIONS } from "PFReducers/growl_reducer";
import PropTypes from "prop-types";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import css from "./import_custom_fields.module.scss";

const ImportCustomFieldsPage = ({ customField, id }) => {
  const { t } = useTranslation("admin");
  const {
    dispatch,
    store: { currentAccount }
  } = useContext(AppContext);
  const { data: currentProfile } = useCurrentProfile();
  const history = useHistory();

  const [send, setSend] = useState(false);
  const [file, setFile] = useState([]);
  const [loading, setloading] = useState(false);
  const [importId, setImportId] = useState(null);
  const [eventListened, setEventListened] = useState(false);
  const [errors, setErrors] = useState([]);
  const { invalidate: invalidateCustomValues } = useCustomValuesInvalidate();

  const { getAccessToken } = useSession();

  const handleCustomValuesImport = useCallback(
    ({ import_id }) => {
      import_id === importId && setEventListened(true);
      return invalidateCustomValues();
    },
    [importId, invalidateCustomValues]
  );
  usePusherEvent(
    `private-profile-${currentProfile.id}`,
    "custom_values_import_completed",
    handleCustomValuesImport
  );

  useEffect(() => {
    if (eventListened) {
      $.ajax({
        api_version: 2,
        url: ApiRoute(`/api/admin/imports/${importId}`)
      }).then((res) => {
        setEventListened(false);
        if (res?.errors?.length > 0) {
          setErrors(res.warnings);
          dispatch({
            type: GROWL_ACTIONS.GROWL_RENDER,
            payload: {
              message: t("customFields.import.processingFail"),
              kind: "error"
            }
          });
        } else {
          setErrors([]);
          dispatch({
            type: GROWL_ACTIONS.GROWL_RENDER,
            payload: {
              message: t("customFields.import.processingSuccess"),
              kind: "success"
            }
          });
        }
      });
    }
  }, [eventListened]);
  const publishAttachments = () => {
    const formData = new FormData();

    formData.append("custom_field_id", [id]);
    formData.append("csv", file[0], file[0].name);
    const req = new XMLHttpRequest();
    req.open("POST", ApiRoute("/api/custom_values/import"));

    req.setRequestHeader("ACCOUNT", currentAccount.full_domain);
    req.setRequestHeader("ACCEPT", `application/vnd.profinda+json;version=2`);
    req.setRequestHeader("Authorization", `Bearer ${getAccessToken()}`);
    req.onloadstart = () => {
      setloading(true);
      dispatch({
        type: GROWL_ACTIONS.GROWL_RENDER,
        payload: {
          message: "Custom fields import started",
          kind: "success"
        }
      });
    };

    req.onloadend = (req) => {
      const res = JSON.parse(req.target.response);
      setloading(false);
      setImportId(res.id);
      if (req?.target.status === 422) {
        req?.target?.onerror();
      }
    };

    req.onerror = () => {
      dispatch({
        type: GROWL_ACTIONS.GROWL_RENDER,
        payload: {
          message: t("customFields.fileFailMessage"),
          kind: "error"
        }
      });
    };
    req.send(formData);
  };

  return (
    <div className={css.wrapper}>
      <div className={css.header}>
        <h1>{t("customFields.import.title")}</h1>
        <h3>{customField?.name}</h3>
      </div>
      {loading && (
        <Alert kind="info" style={{ marginBottom: 20 }}>
          {t("customFields.import.processingTip")}
        </Alert>
      )}
      <PageBox style={{ padding: 20 }}>
        <div className={css.lineWrapper}>
          <div className={css.left}>
            <span style={{ fontWeight: "bold" }}>{t("customFields.import.csvFile")}</span>
          </div>
          <div className={css.right}>
            <label htmlFor="upload">
              <Button kind="primary" tag="a" style={{ margin: "0 12px" }}>
                {t("customFields.import.chooseFile")}
              </Button>
            </label>
            <input
              id="upload"
              type="file"
              style={{ display: "none" }}
              onChange={(event) => {
                setFile(event.target.files);
                setSend(true);
              }}
            />
            {file[0] && <span>{file[0].name}</span>}
          </div>
        </div>
        <div className={css.footer}>
          <Button kind="secondary" onClick={() => history.goBack()}>
            {t("form.buttons.cancel")}
          </Button>
          {send && <Button onClick={() => file && publishAttachments()}>{t("form.buttons.send")}</Button>}
        </div>
        {errors.length > 0 && (
          <div className={css.errorsBlock}>
            <h3>Errors and Warnings</h3>
            <ul>
              {errors.map(({ row, ...rowErrors }, index) => (
                <li key={`error_${index}`}>
                  <b>Row {row}</b>
                  {Object.entries(rowErrors).map(([column, error], rowIndex) => (
                    <p key={`error_${index}_row_${rowIndex}`}>
                      <b>{column}: </b>
                      {error}
                    </p>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        )}
      </PageBox>
    </div>
  );
};

ImportCustomFieldsPage.propTypes = {
  id: PropTypes.string,
  customField: PropTypes.shape({
    name: PropTypes.string
  })
};

export default ImportCustomFieldsPage;
